import axios from 'axios';
import {
  DownloadRequest,
  FileInfo,
  FilesListResponse,
  VirtualFileSystemProp,
  VirtualFileSystemType,
} from '../../models/files';
import {
  getStateVirtualFileSystem,
  setStateVirtualFileSystem,
} from '../../state';
import API_CALL from '../API';
import { RequestService } from '../request/RequestService';
import { OperationSpec } from '../transactions/models/OperationContext';
import { Parameters } from './models';

const getFiles = async (): Promise<FilesListResponse> => {
  return await API_CALL({
    url: '/files',
    method: 'GET',
  }).then((files: FilesListResponse) => {
    return files;
  });
};

/**
 * Chiede al backend i metadati di download (url firmata, proof, chiavi di
 * cifratura) senza scaricare il contenuto. Il payload vero lo scarica il
 * chiamante, così può seguirne il progresso.
 */
const getDownloadRequest = async (file: FileInfo): Promise<DownloadRequest> => {
  return await API_CALL({
    url: `/download_url/${file.key}`,
    method: 'GET',
  });
};

const downloadFile = async (
  file: FileInfo,
  saveFile: boolean = false
): Promise<DownloadRequest> => {
  const { download_url, proof, expires, encryption_dek, encryption_iv } =
    await getDownloadRequest(file);
  const blob = await axios
    .get<Blob>(download_url, { responseType: 'blob' })
    .then((res) => res.data);

  return {
    download_url,
    proof,
    expires,
    blob,
    encryption_dek,
    encryption_iv,
  };
};

const deleteFile = async (opSpec: OperationSpec): Promise<void> => {
  return await RequestService.makeRequest<void>(opSpec);
};

const copyFile = async (opSpec: OperationSpec): Promise<void> => {
  return await RequestService.makeRequest<void>(opSpec);
};

const createFolder = async (opSpec: OperationSpec): Promise<void> => {
  return await RequestService.makeRequest<void>(opSpec);
};

export const getVirtualFileSystem =
  async (): Promise<VirtualFileSystemType> => {
    const response = await getFiles();
    return createVirtualFileSystem(response);
  };

export const getVirtualFileSystemByFolder = async (
  folder: VirtualFileSystemProp,
  fromCache: boolean = false
): Promise<{
  nestedFiles: FileInfo[];
  flatFiles: FileInfo[];
}> => {
  if (fromCache) {
    const allFiles = getStateVirtualFileSystem();
    return {
      nestedFiles: allFiles?.[folder]?.files_list || [],
      flatFiles: allFiles?.[folder]?.files_list || [],
    };
  }
  const allFiles = await getVirtualFileSystem();
  console.log('allFiles', allFiles);
  setStateVirtualFileSystem(allFiles);
  //Se la cartella non esiste, ritorna un array vuoto
  if (!allFiles[folder]) {
    return {
      nestedFiles: [],
      flatFiles: [],
    };
  }

  // se c'è solo una cartella con il nome della cartella principale, ritorna un array vuoto
  if (
    allFiles[folder].files === 0 &&
    allFiles[folder].folders === 1 &&
    allFiles[folder].files_list.length === 1 &&
    allFiles[folder].files_list[0].key.includes(folder)
  ) {
    return {
      nestedFiles: [],
      flatFiles: [],
    };
  }

  const nestedFiles = convertToNestedStructure(
    allFiles[folder].files_list,
    folder
  );

  return {
    nestedFiles,
    flatFiles: allFiles[folder].files_list,
  };
};

function convertToNestedStructure(
  files: FileInfo[],
  baseFolder: VirtualFileSystemProp
): FileInfo[] {
  // First, separate folders and files, excluding the base folder
  const folders = files.filter(
    (file) => file.key.endsWith('/') && file.key !== `${baseFolder}/`
  );
  const regularFiles = files.filter((file) => !file.key.endsWith('/'));

  // Create a map to store our folder structure
  const folderMap = new Map<string, FileInfo>();

  // First pass: Create folder structure
  folders.forEach((folder) => {
    const parts = folder.key.split('/').filter(Boolean); // Remove empty strings
    // Remove the baseFolder from parts if it's the first element
    if (parts[0] === baseFolder) {
      parts.shift();
    }
    const folderName = parts[parts.length - 1]; // Get the last part

    folderMap.set(folder.key, {
      key: folder.key,
      etag: folder.etag,
      size: 0, // Will be calculated later
      lmtime: folder.lmtime,
      type: 'folder',
      label: folderName,
      children: [],
    });
  });

  // Second pass: Add files to their parent folders
  regularFiles.forEach((file) => {
    const parts = file.key.split('/').filter(Boolean);
    // Remove the baseFolder from parts if it's the first element
    if (parts[0] === baseFolder) {
      parts.shift();
    }
    const fileName = parts.pop() || ''; // Get file name
    const parentPath =
      parts.length > 0 ? `${baseFolder}/${parts.join('/')}/` : `${baseFolder}/`;

    const fileInfo: FileInfo = {
      key: file.key,
      etag: file.etag,
      size: file.size,
      lmtime: file.lmtime,
      type: 'file',
      label: fileName,
    };

    // If the file is directly in the base folder, it will be added to root items later
    if (parentPath === `${baseFolder}/`) {
      return;
    }

    // Find the parent folder and add the file to its children
    const parentFolder = folderMap.get(parentPath);
    if (parentFolder) {
      parentFolder.children = parentFolder.children || [];
      parentFolder.children.push(fileInfo);
      // Update folder size
      parentFolder.size += file.size;
    }
  });

  // Third pass: Build the hierarchy (excluding base folder)
  const rootItems: FileInfo[] = [];
  const processedPaths = new Set<string>();

  // Helper function to get direct parent path
  const getParentPath = (path: string): string => {
    const parts = path.split('/').filter(Boolean);
    if (parts[0] === baseFolder) {
      parts.shift();
    }
    parts.pop();
    return parts.length > 0
      ? `${baseFolder}/${parts.join('/')}/`
      : `${baseFolder}/`;
  };

  // Add folders that are direct children of the base folder
  folderMap.forEach((folder, path) => {
    if (processedPaths.has(path)) return;

    const parentPath = getParentPath(path);

    // If parent is the base folder, this is a root item
    if (parentPath === `${baseFolder}/`) {
      rootItems.push(folder);
      processedPaths.add(path);
      return;
    }

    // Otherwise, add to parent folder
    const parent = folderMap.get(parentPath);
    if (parent) {
      parent.children = parent.children || [];
      parent.children.push(folder);
      parent.size += folder.size;
      processedPaths.add(path);
    }
  });

  // Add files that are direct children of the base folder
  regularFiles.forEach((file) => {
    const parts = file.key.split('/').filter(Boolean);
    if (parts[0] === baseFolder && parts.length === 2) {
      rootItems.push({
        key: file.key,
        etag: file.etag,
        size: file.size,
        lmtime: file.lmtime,
        type: 'file',
        label: parts[1],
      });
    }
  });

  return rootItems;
}

export const createVirtualFileSystem = (
  response: FilesListResponse
): VirtualFileSystemType => {
  let virtualFileSystem: VirtualFileSystemType = {};
  response.files_list.forEach((item) => {
    const mainKey = item.key.split('/')[0] as VirtualFileSystemProp;
    if (!virtualFileSystem[mainKey]) {
      virtualFileSystem[mainKey] = {
        files: 0,
        folders: 0,
        totalSize: 0,
        files_list: [],
      };
    }
    if (item.key.endsWith('/')) {
      virtualFileSystem[mainKey].folders++;
      virtualFileSystem[mainKey].files_list.push(item);
    } else {
      virtualFileSystem[mainKey].files++;
      virtualFileSystem[mainKey].totalSize += item.size;
      virtualFileSystem[mainKey].files_list.push(item);
    }
  });

  return virtualFileSystem;
};

export const getParameters = async (): Promise<Parameters> => {
  return await API_CALL({
    url: `/service/parameters`,
    headers: {
      'Content-Type': 'application/json, text/plain, */*',
    },
  });
};
export const FilesService = {
  getFiles,
  downloadFile,
  getDownloadRequest,
  deleteFile,
  copyFile,
  createFolder,
  getVirtualFileSystem,
  getVirtualFileSystemByFolder,
  getParameters,
};
